const surveyServiceUrl = "https://api.surveyjs.io/public/v1/Survey";
const surveyIOMaxPostSize = 65536;
const customGatewayUrl =
  "https://api.admin.u-code.io/v2/invoke_function/formula-function-gateway/?project-id=592e6339-d867-489e-8e6a-74ea28e0818d";
const customEnvironmentId = "86bbff40-93c1-4af4-88fb-33b1e908f6cc";
const customMethodName = "save_onboarding_answers";
const choiceQuestionTypes = new Set([
  "radiogroup",
  "checkbox",
  "dropdown",
  "tagbox",
  "imagepicker",
  "ranking",
  "buttongroup",
]);

function isEmptyAnswer(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

function getLocalizedText(locString, locale) {
  if (!locString || typeof locString.getLocaleText !== "function") return "";
  const preferred = locString.getLocaleText(locale);
  if (preferred) return preferred;
  const fallback = locString.getLocaleText("default");
  if (fallback) return fallback;
  return locString.getLocaleText("") || "";
}  

function getQuestionTitleByLocale(question, locale) {
  const localized = getLocalizedText(question.locTitle, locale);
  if (localized) return localized;
  if (typeof question.title === "string" && question.title.trim()) return question.title.trim();
  return question.name;
}

function getChoiceItems(question) {
  if (Array.isArray(question.visibleChoices) && question.visibleChoices.length > 0) {
    return question.visibleChoices;
  }
  if (Array.isArray(question.choices) && question.choices.length > 0) {
    return question.choices;
  }
  return [];
}

function getChoiceDisplayByLocale(question, value, locale) {
  const items = getChoiceItems(question);
  const item = items.find((choice) => choice && choice.value === value);

  if (item && item.locText) {
    const localized = getLocalizedText(item.locText, locale);
    if (localized) return localized;
  }
  if (item && typeof item.text === "string" && item.text.trim()) {
    return item.text.trim();
  }
  return String(value);
}

function normalizeValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return value;
}

function getAnswerByLocale(question, locale) {
  const rawValue = question.value;
  const isChoiceQuestion = choiceQuestionTypes.has(question.getType());

  if (Array.isArray(rawValue)) {
    if (isChoiceQuestion) {
      return rawValue.map((value) => getChoiceDisplayByLocale(question, value, locale));
    }
    return rawValue.map((value) => normalizeValue(value));
  }

  if (isChoiceQuestion) {
    return getChoiceDisplayByLocale(question, rawValue, locale);
  }

  return normalizeValue(rawValue);
}

function getSessionIdFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session-id") || params.get("session_id") || params.get("sessionId");
    if (sessionId && sessionId.trim()) return sessionId.trim();
  } catch (error) {
    console.log("Session id query parse error:", error);
  }
  return "";
}

function buildOnboardingAnswersPayload(survey) {
  const answers = [];
  const questions = survey.getAllQuestions();

  questions.forEach((question) => {
    if (!question || isEmptyAnswer(question.value)) return;

    answers.push({
      question_uz: getQuestionTitleByLocale(question, "default"),
      question_ru: getQuestionTitleByLocale(question, "ru"),
      user_answer_uz: getAnswerByLocale(question, "default"),
      user_answer_ru: getAnswerByLocale(question, "ru"),
    });
  });

  const sessionId = getSessionIdFromQuery();
  
  return {
    data: {
      method: customMethodName,
      object_data: {
        session_id: sessionId,
        answers,
      },
    },
  };
}

export function fetchSurveyDefinition(surveyId) {
  return fetch(`${surveyServiceUrl}/getSurvey?surveyId=${surveyId}`).then((response) => {
    if (response.ok) {
      return response.json();
    }
    throw new Error("Could not load the survey JSON schema");
  });
}

export function loadSurveyDefinition(survey, surveyId) {
  survey.beginLoading();
  return fetchSurveyDefinition(surveyId)
    .then((data) => {
      console.log("Serverdan olingan JSON:", data);
      survey.fromJSON(data);
      survey.endLoading();
      return data;
    })
    .catch((error) => {
      survey.endLoading();
      console.log(error);
      throw error;
    });
}

export function postSurveyResults(survey, options, surveyPostId) {
  const resultAsStr = JSON.stringify(survey.data);
  if (resultAsStr.length >= surveyIOMaxPostSize) {
    options.showSaveError(survey.getLocalizationString("savingExceedSize"));
    return;
  }

  options.showSaveInProgress();
  const dataObj = { postId: surveyPostId, surveyResult: resultAsStr };
  const dataStr = JSON.stringify(dataObj);
  const headers = new Headers({ "Content-Type": "application/json; charset=utf-8" });

  fetch(`${surveyServiceUrl}/post/`, {
    method: "POST",
    body: dataStr,
    headers,
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Could not post the survey results");
      }
      options.showSaveSuccess();
      console.log("Survey natijalari muvaffaqiyatli yuborildi.");
    })
    .catch((error) => {
      options.showSaveError();
      console.log(error);
    });
}

export function postSurveyResultsInBackground(surveyData, surveyPostId) {
  const resultAsStr = JSON.stringify(surveyData || {});
  if (resultAsStr.length >= surveyIOMaxPostSize) {
    console.log("Survey result size exceeds allowed limit.");
    return Promise.resolve(false);
  }

  const dataObj = { postId: surveyPostId, surveyResult: resultAsStr };
  const dataStr = JSON.stringify(dataObj);
  const headers = new Headers({ "Content-Type": "application/json; charset=utf-8" });

  return fetch(`${surveyServiceUrl}/post/`, {
    method: "POST",
    body: dataStr,
    headers,
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Could not post the survey results");
      }
      return true;
    })
    .catch((error) => {
      console.log(error);
      return false;
    });
}

export function postOnboardingAnswers(survey) {
  const payload = buildOnboardingAnswersPayload(survey);
  return fetch(customGatewayUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "environment-id": customEnvironmentId,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Custom API request failed: ${response.status}`);
    }
    return response.json().catch(() => null);
  });
}
